import "@fortawesome/fontawesome-svg-core/styles.css";
import "../styles/globals.css";

import { Toaster } from "@alta/admin";
import { config } from "@fortawesome/fontawesome-svg-core";
import type { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { SessionProvider, useSession } from "next-auth/react";
import { ReactNode, useEffect } from "react";
import { hotjar } from "react-hotjar";

import EmptyLayout from "../components/Layouts/Empty";
import { AccreditationContextProvider } from "../context/accreditation/accreditationContext";
import { FlagsContextProvider } from "../context/featureFlags/featureFlagsContext";
import {
  getRequestContext,
  LoggingContextProvider,
  RequestContextType,
} from "../context/logging/loggingContext";
import { TradeContextProvider } from "../context/trade/tradeContext";
import {
  INVEST_PAGE_URL,
  TRADE_PAGE_URL,
} from "../lib/data/middleware/urlConstants";
import { checkIfStringStartsWith } from "../lib/helper/checkIfStringStartsWith";
import { useSegmentAnalytics } from "../lib/hooks/useSegmentAnalytics";
import PageWithLayoutType from "../lib/types/pageWithLayouts";

config.autoAddCss = false;

type AppLayoutProps = AppProps & {
  Component: PageWithLayoutType;
  pageProps: any;
  requestContext: RequestContextType;
};

const SEGMENT_TRACKING_AREAS = [
  "/verify",
  INVEST_PAGE_URL,
  TRADE_PAGE_URL,
  "/login",
  "/signup",
  "/portfolio",
  "/kyc",
];
const PROTECTED_ROUTE = [
  "/verify",
  INVEST_PAGE_URL,
  TRADE_PAGE_URL,
  "/kyc",
  "/portfolio",
  "/account/change-investor-type",
];

function MyApp({
  Component,
  pageProps: { session, ...pageProps },
  requestContext,
}: AppLayoutProps) {
  const Layout = Component.layout || EmptyLayout;
  const router = useRouter();
  const { segmentAnalytics } = useSegmentAnalytics();

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV === "production") {
      hotjar.initialize(2667415, 6);
    }
  }, []);

  useEffect(() => {
    // Invoke Segment page call when white listed route changes
    if (checkIfStringStartsWith(router.asPath, SEGMENT_TRACKING_AREAS)) {
      segmentAnalytics.page();
    }
  }, [router.asPath]);

  return (
    <SessionProvider session={session} refetchInterval={5 * 60}>
      <LoggingContextProvider requestContext={requestContext}>
        <FlagsContextProvider flags={[]}>
          <AccreditationContextProvider>
            <TradeContextProvider>
              <Toaster />
              <Layout>
                <Protected>
                  <Head>
                    <meta
                      name="viewport"
                      content="minimum-scale=1, initial-scale=1, width=device-width"
                    />
                  </Head>
                  <Component {...pageProps} />
                </Protected>
              </Layout>
            </TradeContextProvider>
          </AccreditationContextProvider>
        </FlagsContextProvider>
      </LoggingContextProvider>
    </SessionProvider>
  );
}

MyApp.getInitialProps = async ({ Component: C, ctx }: AppContext) => {
  const requestContext = await getRequestContext(ctx.req, ctx.res);
  const pageProps = C && C.getInitialProps ? await C.getInitialProps(ctx) : {};
  return {
    pageProps,
    requestContext,
  };
};

export default MyApp;

function Protected({ children }: { children: ReactNode }): JSX.Element {
  const router = useRouter();

  const _ = useSession({
    required: true,
    onUnauthenticated() {
      if (checkIfStringStartsWith(router.asPath, PROTECTED_ROUTE)) {
        if (checkIfStringStartsWith(router.asPath, ["/invest", "/trade/"])) {
          // delegate route handling to page component
        } else {
          router.push("/login");
        }
      }
    },
  });

  return <>{children}</>;
}
